import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../contexts/CartContext';
import analyticsService from '../services/analyticsService';

/**
 * Acciones de compra de un producto, con su analítica.
 *
 * Existe por dos motivos:
 *
 * 1. El "comprar ahora" estaba copiado literalmente en cinco componentes
 *    (tarjeta del catálogo, destacados y novedades de la home, el banner y la
 *    landing de Ahí les dejo). Ninguna de esas copias reportaba nada, así que
 *    la compra directa era invisible en analítica.
 * 2. Cada punto del sitio necesita declarar su `source` para poder responder
 *    desde dónde se agrega al carrito y desde dónde se compra.
 *
 * @param {string} source Uno de COMMERCE_SOURCES (analyticsService.js).
 */
export default function useProductActions(source) {
  const { addToCart, getItemQuantity } = useCart();
  const navigate = useNavigate();

  const addToCartTracked = useCallback(
    (product, quantity = 1) => {
      if (!product) return;
      addToCart(product, quantity);
      analyticsService.trackProductAddToCart(product, quantity, source);
    },
    [addToCart, source]
  );

  const buyNow = useCallback(
    (product, quantity = 1) => {
      if (!product) return;

      // La analítica va antes de navegar: al cambiar de ruta el componente se
      // desmonta y una petición lanzada después puede quedarse sin enviar.
      analyticsService.trackProductBuyNow(product, quantity, source);

      // No se vacía el carrito. Antes estos cinco sitios hacían clearCart()
      // primero, de modo que comprar un juego borraba en silencio todo lo que
      // el usuario ya había añadido.
      //
      // Si el producto ya estaba en el carrito no se suma otra vez: quien pulsa
      // "comprar ahora" quiere llevarse ese juego, no duplicar lo que tenía.
      if (getItemQuantity(product) === 0) {
        addToCart(product, quantity);
      }

      navigate('/checkout');
    },
    [addToCart, getItemQuantity, navigate, source]
  );

  return { addToCartTracked, buyNow };
}
